import React from "react";
import PropTypes from "prop-types";
//Material imports
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import { styled } from "@mui/material/styles";
//Limitless imports
import { ellipsisText, useKnowledgeLibraryContext } from "../..";
import Paper from "@limitless/modern/surfaces/paper/Paper";
import Typography from "@limitless/modern/data-display/typography/Typography";
import Grid from "@limitless/modern/layout/Grid";
import Button from "@limitless/modern/inputs/button/Button";
import RemoteContent from "../remote-content/RemoteContent";
import AngleDown from "@limitless/icons/AngleDown";
import AngleUp from "@limitless/icons/AngleUp";
import ButtonLoadingAnimation from "../buttons/button-loading-animation/ButtonLoadingAnimation";
const ButtonAccordionSummary = styled(AccordionSummary)(() => ({
  padding: 0,
  minHeight: "auto !important",
  "& .MuiAccordionSummary-content": {
    margin: 0
  }
}));
const KnowledgeArticleTile = ({
  article,
  index,
  expanded
}) => {
  const [copied, setCopied] = React.useState(false);
  const [selectedIndex, setSelectedIndex] = React.useState(null);
  const articleEl = React.useRef(null);
  const {
    selectedArticle,
    updateSelectedArticle,
    clearSelectedArticle
  } = useKnowledgeLibraryContext();
  const {
    selectedArticleIndex,
    answer,
    loading
  } = selectedArticle;
  const getBodyText = () => {
    //default in case title is over 120 chars
    let cutOff = 50;
    // if the title of the article can be quite long
    // so we change our ellipsis cutOff based on the title length
    // if title length over 60 it takes up two lines and hence we need a lower cutoff
    if (article.question.length < 60) {
      cutOff = 160;
    } else if (article.question.length < 120) {
      cutOff = 120;
    }
    const tmp = document.createElement("DIV");
    tmp.innerHTML = article.answer;

    //strip out the HTML so we can properly use ellipsis
    return ellipsisText(tmp.textContent, cutOff);
  };
  const handleContinueReadingExapand = (e, isExpanded) => {
    setSelectedIndex(isExpanded ? index : null);
    if (isExpanded) {
      updateSelectedArticle({
        ...article,
        selectedArticleIndex: index
      });
    } else {
      clearSelectedArticle();
    }
  };
  const handleCopyClick = () => {
    updateSelectedArticle({
      ...article,
      selectedArticleIndex: index
    }, fullArticle => {
      if (fullArticle) {
        setCopied(true);
      }
    });
  };
  React.useEffect(() => {
    if (copied && answer) {
      var _navigator$clipboard;
      if (typeof ((_navigator$clipboard = navigator.clipboard) === null || _navigator$clipboard === void 0 ? void 0 : _navigator$clipboard.writeText) === "function") {
        navigator.clipboard.writeText(answer);
        setCopied(true);
      }
    }
  }, [copied, answer]);
  const showContinueReadingButton = getBodyText().length !== article.answer.length;
  return /*#__PURE__*/React.createElement(Paper, {
    sx: {
      backgroundColor: theme => theme.palette.background.default,
      padding: "24px 32px",
      marginTop: 2,
      boxShadow: theme => theme.palette.mode === "light" ? "0px 2px 5px -2px rgba(0,0,0,0.15)" : "2px 0px 31px -2px rgba(0,0,0,0.4)"
    },
    key: index
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "subtitle2",
    sx: {
      color: theme => theme.palette.text.primary
    }
  }, article.question), /*#__PURE__*/React.createElement(Accordion, {
    onChange: handleContinueReadingExapand,
    sx: {
      backgroundColor: theme => theme.palette.background.default,
      boxShadow: "none !important"
    },
    expanded: expanded
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(AccordionDetails, {
    sx: {
      padding: 0
    }
  }, /*#__PURE__*/React.createElement(Grid, {
    mt: 2
  }, answer && expanded ? /*#__PURE__*/React.createElement(RemoteContent, {
    wordBreak: "break-word",
    content: answer,
    ref: articleEl,
    id: "kb-article"
  }) : /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    sx: {
      color: theme => theme.palette.text.primary,
      wordBreak: "break-word"
    }
  }, getBodyText())), /*#__PURE__*/React.createElement(Grid, {
    container: true,
    justifyContent: "space-between",
    alignItems: "center",
    direction: "row",
    mt: 2
  }, /*#__PURE__*/React.createElement(ButtonAccordionSummary, null, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    ml: -3
  }, showContinueReadingButton && /*#__PURE__*/React.createElement(Button, {
    size: "small"
  }, expanded ? "Close" : "Continue Reading", expanded ? /*#__PURE__*/React.createElement(AngleUp, null) : /*#__PURE__*/React.createElement(AngleDown, null)))), /*#__PURE__*/React.createElement(Grid, {
    mr: 0.25
  }, /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "outlined",
    onClick: handleCopyClick
  }, copied && selectedArticleIndex === index ? "Copied" : "Copy", /*#__PURE__*/React.createElement(ButtonLoadingAnimation, {
    loading: loading && selectedArticleIndex === index && selectedIndex !== index
  }))))))));
};
export default KnowledgeArticleTile;
KnowledgeArticleTile.propTypes = {
  article: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  expanded: PropTypes.bool.isRequired
};